/**
 * Secure Storage Utility
 * Provides secure token storage with encryption and XSS protection
 */

// Simple encryption for localStorage (better than plain text)
// In production, consider using a more robust solution
const ENCRYPTION_KEY = 'airkeyhub_secure_key_2024';

/**
 * Simple XOR encryption for localStorage
 * Note: This is basic obfuscation. For production, consider using Web Crypto API
 */
function encrypt(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length)
    );
  }
  return btoa(result); // Base64 encode
}

function decrypt(encoded: string): string {
  try {
    const text = atob(encoded); // Base64 decode
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length)
      );
    }
    return result;
  } catch (error) {
    console.error('Decryption error:', error);
    return '';
  }
}

/**
 * Secure storage interface
 */
export const secureStorage = {
  /**
   * Set item in secure storage
   */
  setItem(key: string, value: string): void {
    try {
      const encrypted = encrypt(value);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Error storing item:', error);
    }
  },

  /**
   * Get item from secure storage
   */
  getItem(key: string): string | null {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;
      const decrypted = decrypt(encrypted);
      // If decryption failed (returns empty string), remove the corrupted item
      if (!decrypted) {
        console.warn('Corrupted data detected, clearing item:', key);
        this.removeItem(key);
        return null;
      }
      return decrypted;
    } catch (error) {
      console.error('Error retrieving item:', error);
      // Clear corrupted data
      this.removeItem(key);
      return null;
    }
  },

  /**
   * Remove item from secure storage
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  },

  /**
   * Clear all items from secure storage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  },

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Only check expiration if exp field exists
      if (payload.exp) {
        const exp = payload.exp * 1000; // Convert to milliseconds
        return Date.now() >= exp;
      }
      // If no exp field, token doesn't expire
      return false;
    } catch (error) {
      // If we can't parse, don't assume expired - just return the token
      console.warn('Token parsing error:', error);
      return false;
    }
  },

  /**
   * Get token with expiry check
   */
  getToken(key: string): string | null {
    try {
      const token = this.getItem(key);
      if (!token) return null;
      
      // Validate token format (should be a JWT with 3 parts)
      if (typeof token !== 'string' || token.split('.').length !== 3) {
        console.warn('Invalid token format, clearing:', key);
        this.removeItem(key);
        return null;
      }
      
      // Check if token is expired (only if it has exp field)
      if (this.isTokenExpired(token)) {
        this.removeItem(key);
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      this.removeItem(key);
      return null;
    }
  }
};

/**
 * Session storage utility (for sensitive temporary data)
 */
export const secureSessionStorage = {
  setItem(key: string, value: string): void {
    try {
      const encrypted = encrypt(value);
      sessionStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Error storing session item:', error);
    }
  },

  getItem(key: string): string | null {
    try {
      const encrypted = sessionStorage.getItem(key);
      if (!encrypted) return null;
      return decrypt(encrypted);
    } catch (error) {
      console.error('Error retrieving session item:', error);
      return null;
    }
  },

  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing session item:', error);
    }
  },

  clear(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing session storage:', error);
    }
  }
};

export default secureStorage;